/**
 * 厂商展开行 - 游戏列表组件
 * @description 表格行展开后展示该厂商下的游戏数据，内部管理排序/分页状态
 */
import React from 'react';
import { useIntl } from 'umi';
import Loading from '@intelligence/components/Loading';
import NoData from '@intelligence/components/NoData';
import PublisherTable from '../PublisherTable';
import useLocalTableState, { createDefaultSorts } from '../../hooks/useLocalTableState';
import { isEmpty } from '@intelligence/v3/utils/common';
import { DEFAULT_PAGE_SIZE, TOP_PUBLISHERS_TABLE_TIPS_I18N } from '../../constants';

import type { ICasualCompetitorsDataSourceProps } from '../../types';

import './index.scss';

interface IExpandedPublisherGamesProps extends IBaseProps, ICasualCompetitorsDataSourceProps {
  loading?: boolean;
  topPublishersTableTipsI18n?: IRecord<IRecord<string>>;
  games?: IRecord[];
  hasRevenue?: boolean;
  granularity?: string;
  // 当前周期开始日期（用于环比气泡）
  startDate?: string;
  // 当前周期结束日期（用于环比气泡）
  endDate?: string;
  genreOptionsMap?: IRecord<any[]>;
  onSortStateChange?: (sortState: IRatioMetricSortState<IRecord> | undefined) => void;
}

const ExpandedPublisherGames: React.FC<IExpandedPublisherGamesProps> = (props) => {
  const {
    loading = false,
    topPublishersTableTipsI18n = TOP_PUBLISHERS_TABLE_TIPS_I18N,
    dataSource,
    games = [],
    hasRevenue = true,
    granularity = 'monthly',
    startDate,
    endDate,
    genreOptionsMap,
    onSortStateChange,
  } = props;

  const { formatMessage } = useIntl();

  const { sorts, pagingQuery, handleSortsChange, handlePagingChange } = useLocalTableState<IRecord>({
    defaultSorts: createDefaultSorts(dataSource, 'daily_avg_dau'),
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  if (loading) {
    return (
      <div className="dbi-flex-center">
        <Loading style={{ height: 200 }} />
      </div>
    );
  }

  if (isEmpty(games)) {
    return (
      <div className="dbi-flex-center">
        <NoData style={{ height: 200 }} />
      </div>
    );
  }

  const tipsI18nKeys = topPublishersTableTipsI18n?.[granularity || 'monthly'];
  const activeRatioMetricName = formatMessage({
    id: 'dbh_studio_casual_company_col_active_ratio',
  });

  const handleSortsWithSync = (nextSorts: IRatioMetricSortState<IRecord>[]) => {
    onSortStateChange?.(nextSorts[0]);
    handleSortsChange(nextSorts);
  };

  return (
    <PublisherTable
      className="dbh-top-publishers-games-table"
      bordered={false}
      isLastColumn
      showSequence
      showPublisherInfo={false}
      isMetricColumnAutoWidth={true}
      hasRevenue={hasRevenue}
      dataSource={dataSource}
      granularity={granularity}
      startDate={startDate}
      endDate={endDate}
      genreOptionsMap={genreOptionsMap}
      data={games}
      sorts={sorts}
      onSortsChange={handleSortsWithSync}
      pagingQuery={pagingQuery}
      onPagingChange={handlePagingChange}
      tipsI18nKeys={tipsI18nKeys}
      activeRatioMetricName={activeRatioMetricName}
      gameColumnWidth={hasRevenue ? 246 : 310}
      activeRatioMetricColumnWidth={hasRevenue ? 165 : 310}
      // activeRatioMetricColumnWidth={249}
    />
  );
};

export default ExpandedPublisherGames;
